import { pdf } from '@react-pdf/renderer';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import type { SearchResult } from '@/lib/types/search';
import { parseDescription } from './descriptionParser';
import type { ThreatProfileResponse, Attacker, Campaign, AttackerTTP, RemediationStrategy } from '@/lib/types/threatProfile';

const commonStyles = {
  page: {
    padding: 30,
    backgroundColor: '#ffffff',
    fontFamily: 'Helvetica',
    fontSize: 10,
    lineHeight: 1.4,
  },
  header: {
    backgroundColor: '#1E40AF',
    color: '#ffffff',
    paddingHorizontal: 30,
    paddingVertical: 20,
    marginBottom: 10,
    marginHorizontal: -30,
    marginTop: -30,
  },
  headerTitle: {
    fontSize: 24,
    marginBottom: 10,
    fontWeight: 'bold' as 'bold',
    color: '#ffffff',
  },
  headerDate: {
    fontSize: 10,
    opacity: 0.8,
    color: '#ffffff',
  },
  section: {
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 16,
    color: '#1E40AF',
    marginBottom: 10,
    borderBottomWidth: 1,
    borderBottomColor: '#D1D5DB',
    paddingBottom: 5,
    fontWeight: 'bold' as 'bold',
  },
  subSectionTitle: {
    fontSize: 13,
    color: '#1F2937',
    marginBottom: 8,
    fontWeight: 'bold' as 'bold',
  },
  text: {
    color: '#374151',
    marginBottom: 3,
  },
  listItem: {
    marginBottom: 4,
    paddingLeft: 10,
    color: '#374151',
  },
  badge: { 
    backgroundColor: '#DBEAFE', 
    color: '#1E40AF', 
    paddingHorizontal: 5,
    paddingVertical: 1,
    borderRadius: 3,
    fontSize: 9,
    marginRight: 4,
    marginBottom: 4,
  },
  card: {
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 5,
    padding: 12,
    marginBottom: 12,
    backgroundColor: '#F9FAFB',
  },
  attackerName: {
    fontSize: 20,
    color: '#111827',
    marginBottom: 12,
    fontWeight: 'bold' as 'bold',
  },
  campaignName: {
    fontSize: 11,
    fontWeight: 'bold' as 'bold',
    color: '#1D4ED8',
    marginBottom: 4,
  },
  ttpTechniqueName: {
    fontSize: 11,
    fontWeight: 'bold' as 'bold',
    color: '#059669',
    marginBottom: 4,
  },
  remediationItem: {
    marginBottom: 10,
    paddingBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#F3F4F6',
  },
  remediationCategory: {
    fontSize: 10,
    fontWeight: 'bold' as 'bold',
    color: '#4B5563',
    marginBottom: 2,
  },
  remediationPriority: {
    fontSize: 9,
    fontStyle: 'italic' as 'italic',
    color: '#EF4444',
    marginBottom: 3,
  },
  // Specific styles for ThreatReport (SearchResult based) - RESTORED
  threatContainer: {
    marginBottom: 15,
  },
  threatHeader: {
    backgroundColor: '#F3F4F6', // Tailwind gray-100
    padding: 15,
    marginBottom: 15,
    borderRadius: 4,
  },
  threatTitle: {
    fontSize: 20, 
    color: '#1E40AF',
    marginBottom: 8,
    fontWeight: 'bold' as 'bold',
  },
  threatMeta: {
    flexDirection: 'row' as 'row',
    flexWrap: 'wrap' as 'wrap',
    marginTop: 8,
  },
  metaItem: {
    fontSize: 10, 
    color: '#4B5563', // Tailwind gray-600
    marginRight: 15, 
    marginBottom: 5,
  },
  divider: {
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB', // Tailwind gray-200
    marginVertical: 15,
  },
};

// @ts-ignore
const styles = StyleSheet.create(commonStyles);

const ThreatReport = ({ data }: { data: SearchResult[] }) => (
  <Document>
    {data.map((threat, index) => (
      <Page key={`${threat.group_id}-${index}`} size="A4" style={styles.page}>
        {index === 0 && (
          <View style={styles.header} fixed>
            <Text style={styles.headerTitle}>Pax Intelligence Threat Report</Text>
            <Text style={styles.headerDate}>{new Date().toLocaleDateString()}</Text>
          </View>
        )}
        <View style={styles.threatContainer}>
          <View style={styles.threatHeader}>
            <Text style={styles.threatTitle}>{threat.common_name}</Text>
            <View style={styles.threatMeta}>
              <Text style={styles.metaItem}>ID: {threat.group_id}</Text>
              <Text style={styles.metaItem}>Country: {threat.coutry}</Text>
              <Text style={styles.metaItem}>Year: {threat.year}</Text>
            </View>
          </View>
          <View style={styles.section} wrap>
            <Text style={styles.sectionTitle}>Overview</Text>
            <Text style={styles.text}>{parseDescription(threat.description)}</Text>
          </View>
          {threat.ttps && threat.ttps.length > 0 && (
            <View style={styles.section} wrap>
              <Text style={styles.sectionTitle}>TTPs</Text>
              {threat.ttps.map((ttp, i) => (
                <Text key={i} style={styles.listItem}>• {ttp.name}</Text>
              ))}
            </View>
          )}
          {threat.software && threat.software.length > 0 && (
            <View style={styles.section} wrap>
              <Text style={styles.sectionTitle}>Associated Software</Text>
              {threat.software.map((sw, i) => (
                <Text key={i} style={styles.listItem}>• {sw.name}</Text>
              ))}
            </View>
          )}
        </View>
        {index < data.length - 1 && <View style={styles.divider} />}
      </Page>
    ))}
  </Document>
);

export async function generatePDF(data: SearchResult[]) {
  try {
    const blob = await pdf(<ThreatReport data={data} />).toBlob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `pax-intelligence-search-report-${new Date().toISOString().split('T')[0]}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error generating SearchResult PDF:', error);
  }
}

const ThreatProfileReportDocument = ({ profile }: { profile: ThreatProfileResponse }) => {
  const processedAttackers = profile.attackers.map(attacker => {
    let firstSeen = attacker.first_seen;
    if (attacker.latest_campaigns && attacker.latest_campaigns.length > 0) {
      const campaignStartDates = attacker.latest_campaigns
        .map(c => c.start_date ? new Date(c.start_date).getTime() : Infinity)
        .filter(date => date !== Infinity);
      if (campaignStartDates.length > 0) {
        firstSeen = new Date(Math.min(...campaignStartDates)).toISOString();
      }
    }
    return { ...attacker, first_seen: firstSeen };
  });

  // Sort attackers by overall_risk_score descending
  processedAttackers.sort((a, b) => (b.overall_risk_score ?? 0) - (a.overall_risk_score ?? 0));

  return (
    <Document author="Pax Intelligence" title={`Pax Intelligence - AI Threat Profile Report - ${new Date().toLocaleDateString()}`}>
      {processedAttackers.map((attacker: Attacker) => {
        const lastSeenDate = attacker.last_seen_activity ? new Date(attacker.last_seen_activity) : null;
        const threeMonthsAgoDisplay = new Date();
        threeMonthsAgoDisplay.setMonth(threeMonthsAgoDisplay.getMonth() - 3);
        const showLastSeen = lastSeenDate && lastSeenDate >= threeMonthsAgoDisplay;

        let displayName = attacker.attacker_name;
        const akaMatch = attacker.attacker_name.match(/\(aka\s+([^)]+)\)/i);

        if (akaMatch && attacker.also_known_as && attacker.also_known_as.length > 0) {
          displayName = attacker.also_known_as[0];
        } else if (akaMatch) {
          displayName = akaMatch[1];
        }

        return (
        <Page key={attacker.attacker_name} size="A4" style={styles.page} wrap>
          <View style={styles.header} fixed>
            <Text style={styles.headerTitle}>Pax Intelligence - AI Threat Profile Report</Text>
            <Text style={styles.headerDate}>{new Date().toLocaleDateString()}</Text>
          </View>
          <View style={{ paddingTop: 95 }} wrap={true}> {/* Increased paddingTop and enabled wrapping */}
            <View style={styles.section} wrap={true}>
              {displayName && <Text style={styles.attackerName}>{displayName}</Text>}
              {attacker.also_known_as && attacker.also_known_as.length > 0 && <Text style={styles.text}>(Also known as: {attacker.also_known_as.join(', ')})</Text>}
              {attacker.identification_rationale && <Text style={styles.text}>Identification Rationale: {attacker.identification_rationale}</Text>}
              {attacker.enrichment_status && <Text style={styles.text}>Enrichment Status: <Text style={styles.badge}>{attacker.enrichment_status}</Text></Text>}
              {attacker.attribution_confidence && <Text style={styles.text}>Attribution Confidence: <Text style={styles.badge}>{attacker.attribution_confidence}</Text></Text>}
              {attacker.first_seen && <Text style={styles.text}>First Seen: {new Date(attacker.first_seen).toLocaleDateString()}</Text>}
              {showLastSeen && attacker.last_seen_activity && <Text style={styles.text}>Last Seen Activity: {new Date(attacker.last_seen_activity).toLocaleDateString()}</Text>}
              {attacker.motivation && attacker.motivation.length > 0 && <Text style={styles.text}>Motivation: {attacker.motivation.join(', ')}</Text>}
              {attacker.target_sectors && attacker.target_sectors.length > 0 && <Text style={styles.text}>Target Sectors: {attacker.target_sectors.map(s => <Text key={s} style={styles.badge}>{s}</Text>)}</Text>}
              {attacker.target_countries && attacker.target_countries.length > 0 && <Text style={styles.text}>Target Countries: {attacker.target_countries.map(c => <Text key={c} style={styles.badge}>{c}</Text>)}</Text>}
              {typeof attacker.overall_risk_score === 'number' && <Text style={styles.text}>Overall Risk Score: <Text style={{fontWeight: 'bold' as 'bold', color: attacker.overall_risk_score > 7 ? '#DC2626' : attacker.overall_risk_score > 4 ? '#F59E0B' : '#10B981' }}>{attacker.overall_risk_score}</Text></Text>}

              {attacker.risk_factors && (
                <View style={styles.card}>
                  <Text style={styles.subSectionTitle}>Risk Factors</Text>
                  {typeof attacker.risk_factors.sophistication === 'number' && <Text style={styles.text}>Sophistication: {attacker.risk_factors.sophistication}</Text>}
                  {typeof attacker.risk_factors.targeting_relevance === 'number' && <Text style={styles.text}>Targeting Relevance: {attacker.risk_factors.targeting_relevance}</Text>}
                  {typeof attacker.risk_factors.persistence === 'number' && <Text style={styles.text}>Persistence: {attacker.risk_factors.persistence}</Text>}
                  {typeof attacker.risk_factors.impact_potential === 'number' && <Text style={styles.text}>Impact Potential: {attacker.risk_factors.impact_potential}</Text>}
                </View>
              )}

              {attacker.latest_campaigns && attacker.latest_campaigns.length > 0 && (
                <View style={styles.card}>
                  <Text style={styles.subSectionTitle}>Attributed Campaigns</Text>
                  {attacker.latest_campaigns.map((campaign: Campaign) => {
                    const campaignStatusLower = campaign.status?.toLowerCase();
                    const showStatus = campaignStatusLower !== 'concluded';
                    return (
                      <View key={campaign.campaign_name} style={{ marginBottom: 15, paddingBottom: 8, borderBottomWidth: 1, borderBottomColor: '#F3F4F6' }}>
                        {campaign.campaign_name && <Text style={styles.campaignName}>{campaign.campaign_name}</Text>}
                        {showStatus && campaign.status && <Text style={styles.text}>Status: <Text style={styles.badge}>{campaign.status}</Text></Text>}
                        {campaign.start_date && <Text style={styles.text}>Started: {new Date(campaign.start_date).toLocaleDateString()}</Text>}
                        {campaign.description && <Text style={styles.text}>Description: {campaign.description}</Text>}
                        {campaign.targets && campaign.targets.length > 0 && (
                          <View style={{ flexDirection: 'row' as 'row', flexWrap: 'wrap' as 'wrap', marginTop: 6 }}>
                             <Text style={[styles.text, {fontWeight: 'bold' as 'bold'}]}>Targets: </Text>
                            {campaign.targets.map(target => (
                              <Text key={target} style={[styles.text, styles.badge]}>{target}</Text>
                            ))}
                          </View>
                        )}
                        {campaign.affected_geographies && campaign.affected_geographies.length > 0 && <Text style={styles.text}>Affected Geographies: {campaign.affected_geographies.join(', ')}</Text>}
                        {campaign.attribution_source && <Text style={styles.text}>Attribution Source: {campaign.attribution_source}</Text>}
                        {typeof campaign.campaign_risk_score === 'number' && <Text style={styles.text}>Campaign Risk Score: <Text style={{fontWeight: 'bold' as 'bold'}}>{campaign.campaign_risk_score}</Text></Text>}
                        {campaign.campaign_risk_rationale && <Text style={styles.text}>Risk Rationale: {campaign.campaign_risk_rationale}</Text>}
                        {campaign.observed_malware_families && campaign.observed_malware_families.length > 0 && <Text style={styles.text}>Observed Malware: {campaign.observed_malware_families.join(', ')}</Text>}
                        {campaign.vulnerabilities_exploited && campaign.vulnerabilities_exploited.length > 0 && <Text style={styles.text}>Vulnerabilities Exploited: {campaign.vulnerabilities_exploited.join(', ')}</Text>}
                      </View>
                    );
                  })}
                </View>
              )}

              {attacker.attacker_ttps && attacker.attacker_ttps.length > 0 && (
                <View style={styles.card}>
                  <Text style={styles.subSectionTitle}>Highly Used TTPs</Text>
                  {attacker.attacker_ttps.map((ttp: AttackerTTP) => (
                    <View key={ttp.ttp_id} style={{ marginBottom: 12, paddingBottom: 6, borderBottomWidth: 1, borderBottomColor: '#F3F4F6' }}>
                      {ttp.technique_name && <Text style={styles.ttpTechniqueName}>{ttp.technique_name} {ttp.ttp_id && <Text style={{fontSize: 9, color: '#6B7280'}}>({ttp.ttp_id})</Text>}</Text>}
                      {ttp.phase && <Text style={styles.text}>Phase: <Text style={styles.badge}>{ttp.phase}</Text></Text>}
                      {ttp.description && <Text style={styles.text}>{ttp.description}</Text>}
                      {ttp.platforms && ttp.platforms.length > 0 && <Text style={styles.text}>Platforms: {ttp.platforms.join(', ')}</Text>}
                    </View>
                  ))}
                </View>
              )}

              {attacker.remediation_strategies && attacker.remediation_strategies.length > 0 && (
                <View style={styles.card}>
                  <Text style={styles.subSectionTitle}>Remediation Strategies</Text>
                  {attacker.remediation_strategies.map((strategy: RemediationStrategy, index: number) => (
                    <View key={`${strategy.category}-${index}`} style={styles.remediationItem}>
                      {strategy.category && <Text style={styles.remediationCategory}>{strategy.category}</Text>}
                      {strategy.recommendation && <Text style={styles.text}>{strategy.recommendation}</Text>}
                      {strategy.priority && <Text style={styles.remediationPriority}>Priority: {strategy.priority}</Text>}
                      {strategy.related_ttp_ids && strategy.related_ttp_ids.length > 0 && (
                         <Text style={styles.text}>Related TTPs: {strategy.related_ttp_ids.join(', ')}</Text>
                      )}
                    </View>
                  ))}
                </View>
              )}
            </View>
          </View>
        </Page>
      );
    })}
    </Document>
  );
};

export async function generateThreatProfilePDF(profileData: ThreatProfileResponse) {
  if (!profileData || !profileData.attackers || profileData.attackers.length === 0) {
    console.warn('No threat profile data to generate PDF.');
    return;
  }
  try {
    const blob = await pdf(<ThreatProfileReportDocument profile={profileData} />).toBlob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Pax-AI-Threat-Profile-${profileData.attackers.map(a=>a.attacker_name).join('_')}-${new Date().toISOString().split('T')[0]}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error generating Threat Profile PDF:', error);
  }
}